import {
  PUBLIC_LOCALES,
  PUBLISHED_PUBLIC_LOCALES,
  PUBLIC_ORIGINS,
  publicPath,
  type PublicLocale,
} from './locale';

export const PUBLIC_ROUTES = [
  'app',
  'partners',
  'coverage',
  'privacy',
  'terms',
] as const;
export type PublicRoute = (typeof PUBLIC_ROUTES)[number];
export type RouteName = PublicRoute | 'cityops' | 'notfound';

export function routeFromPath(
  pathname: string,
  locale?: PublicLocale,
): RouteName {
  const path = pathname.replace(/\/$/, '') || '/';
  if (path === '/cityops' || path.startsWith('/cityops/')) return 'cityops';
  for (const route of PUBLIC_ROUTES) {
    if (
      (locale ? [locale] : PUBLIC_LOCALES).some(
        (locale) => publicPath(route, locale) === path,
      )
    )
      return route;
  }
  return 'notfound';
}

const metadata: Record<
  PublicLocale,
  Record<PublicRoute, { title: string; description: string }>
> = {
  cs: {
    app: {
      title: 'Parknuto — ukázka mapy parkovacích zón Praha',
      description:
        'Prohlédněte si živá data pražských parkovacích zón bez registrace a SPZ. Platby přes Parknuto zatím nejsou dostupné. Najdete zde odkaz na oficiální platbu města.',
    },
    partners: {
      title: 'Parknuto pro města — příprava napojení parkovacích systémů',
      description:
        'Podklady pro jednání měst a provozovatelů s Parknuto: smluvní, technické a provozní vstupy, postup spolupráce a podmínky budoucího spuštění.',
    },
    coverage: {
      title: 'Pokrytí Parknuto — města v přípravě',
      description:
        'Aktuální rozsah Parknuto: platby zatím nejsou dostupné v žádném městě. Seznam měst v přípravě neznamená spuštěnou službu ani uzavřenou spolupráci.',
    },
    privacy: {
      title: 'Ochrana soukromí — Parknuto',
      description:
        'Jak veřejná ukázka Parknuto zpracovává polohu, e-mailovou komunikaci a technické logy. Analytika se zapíná pouze s vaším souhlasem.',
    },
    terms: {
      title: 'Podmínky veřejné ukázky — Parknuto',
      description:
        'Rozsah a podmínky používání veřejné ukázky Parknuto. Mapová data jsou informační; web nevytváří parkovací oprávnění ani neprovádí platbu.',
    },
  },
  sk: {
    app: {
      title: 'Parknuto — ukážka mapy parkovacích zón Praha',
      description:
        'Prezrite si živé dáta pražských parkovacích zón bez registrácie a EČV. Platby cez Parknuto zatiaľ nie sú dostupné. Nájdete tu odkaz na oficiálnu platbu mesta.',
    },
    partners: {
      title: 'Parknuto pre mestá — príprava pripojenia parkovacích systémov',
      description:
        'Podklady na rokovanie miest a prevádzkovateľov s Parknuto: zmluvné, technické a prevádzkové vstupy, postup spolupráce a podmienky budúceho spustenia.',
    },
    coverage: {
      title: 'Pokrytie Parknuto — mestá v príprave',
      description:
        'Aktuálny rozsah Parknuto: platby zatiaľ nie sú dostupné v žiadnom meste. Zoznam miest v príprave neznamená spustenú službu ani uzatvorenú spoluprácu.',
    },
    privacy: {
      title: 'Ochrana súkromia — Parknuto',
      description:
        'Ako verejná ukážka Parknuto spracúva polohu, e-mailovú komunikáciu a technické logy. Analytika sa zapína iba s vaším súhlasom.',
    },
    terms: {
      title: 'Podmienky verejnej ukážky — Parknuto',
      description:
        'Rozsah a podmienky používania verejnej ukážky Parknuto. Mapové dáta sú informačné; web nevytvára parkovacie oprávnenie ani nevykonáva platbu.',
    },
  },
  en: {
    app: {
      title: 'Parknuto — explore Prague parking zones',
      description:
        'Explore live Prague parking zone data without registering or entering a number plate. Parknuto payments are not available yet. Find the official city payment link.',
    },
    partners: {
      title: 'Parknuto for cities — preparing parking integrations',
      description:
        'Information for cities and parking operators considering Parknuto: contractual, technical and operational requirements, next steps and conditions for a future launch.',
    },
    coverage: {
      title: 'Parknuto coverage — cities in preparation',
      description:
        'Parknuto payments are not available in any city yet. A city listed as in preparation does not mean an active service or an agreed partnership.',
    },
    privacy: {
      title: 'Privacy — Parknuto',
      description:
        'How the public Parknuto demo handles location, email correspondence and technical logs. Analytics is enabled only with your consent.',
    },
    terms: {
      title: 'Public demo terms — Parknuto',
      description:
        'Scope and terms of the public Parknuto demo. Map data is for information; this website does not issue parking permits or process parking payments.',
    },
  },
  de: {
    app: {
      title: 'Parknuto — Parkzonen in Prag entdecken',
      description:
        'Aktuelle Daten zu Prager Parkzonen ohne Registrierung oder Kennzeichen ansehen. Zahlungen über Parknuto sind noch nicht verfügbar. Mit Link zur offiziellen Zahlung der Stadt.',
    },
    partners: {
      title: 'Parknuto für Städte — Vorbereitung von Parksystem-Anbindungen',
      description:
        'Informationen für Städte und Parkraumbetreiber: vertragliche, technische und betriebliche Voraussetzungen, nächste Schritte und Bedingungen für einen künftigen Start.',
    },
    coverage: {
      title: 'Parknuto Abdeckung — Städte in Vorbereitung',
      description:
        'Zahlungen über Parknuto sind noch in keiner Stadt verfügbar. Eine Stadt in Vorbereitung bedeutet weder einen aktiven Dienst noch eine vereinbarte Partnerschaft.',
    },
    privacy: {
      title: 'Datenschutz — Parknuto',
      description:
        'So verarbeitet die öffentliche Parknuto-Demo Standortdaten, E-Mail-Korrespondenz und technische Protokolle. Analysefunktionen werden nur mit Ihrer Einwilligung aktiviert.',
    },
    terms: {
      title: 'Nutzungsbedingungen der öffentlichen Demo — Parknuto',
      description:
        'Umfang und Bedingungen der öffentlichen Parknuto-Demo. Kartendaten dienen der Information; die Website stellt keine Parkberechtigung aus und wickelt keine Parkzahlung ab.',
    },
  },
  es: {
    app: {
      title: 'Parknuto — explora las zonas de aparcamiento de Praga',
      description:
        'Consulta datos actuales de las zonas de aparcamiento de Praga sin registro ni matrícula. Los pagos con Parknuto aún no están disponibles. Enlace al pago oficial de la ciudad.',
    },
    partners: {
      title: 'Parknuto para ciudades — preparación de integraciones',
      description:
        'Información para ciudades y operadores de aparcamiento: requisitos contractuales, técnicos y operativos, próximos pasos y condiciones para una futura puesta en marcha.',
    },
    coverage: {
      title: 'Cobertura de Parknuto — ciudades en preparación',
      description:
        'Los pagos con Parknuto aún no están disponibles en ninguna ciudad. Una ciudad en preparación no implica un servicio activo ni un acuerdo de colaboración.',
    },
    privacy: {
      title: 'Privacidad — Parknuto',
      description:
        'Cómo trata la demostración pública de Parknuto la ubicación, los correos y los registros técnicos. La analítica solo se activa con tu consentimiento.',
    },
    terms: {
      title: 'Condiciones de la demostración pública — Parknuto',
      description:
        'Alcance y condiciones de la demostración pública de Parknuto. El mapa es informativo; esta web no emite autorizaciones ni tramita pagos de aparcamiento.',
    },
  },
  it: {
    app: {
      title: 'Parknuto — esplora le zone di sosta di Praga',
      description:
        'Consulta i dati aggiornati delle zone di sosta di Praga senza registrazione né targa. I pagamenti con Parknuto non sono ancora disponibili. Link al pagamento ufficiale della città.',
    },
    partners: {
      title: 'Parknuto per le città — preparazione delle integrazioni',
      description:
        'Informazioni per città e gestori della sosta: requisiti contrattuali, tecnici e operativi, prossimi passi e condizioni per una futura attivazione.',
    },
    coverage: {
      title: 'Copertura di Parknuto — città in preparazione',
      description:
        'I pagamenti con Parknuto non sono ancora disponibili in nessuna città. Una città in preparazione non indica un servizio attivo né un accordo di collaborazione.',
    },
    privacy: {
      title: 'Privacy — Parknuto',
      description:
        'Come la demo pubblica di Parknuto tratta posizione, corrispondenza email e log tecnici. Le analisi vengono attivate solo con il tuo consenso.',
    },
    terms: {
      title: 'Condizioni della demo pubblica — Parknuto',
      description:
        'Ambito e condizioni della demo pubblica di Parknuto. La mappa è informativa; questo sito non emette autorizzazioni né elabora pagamenti per la sosta.',
    },
  },
  pl: {
    app: {
      title: 'Parknuto — poznaj strefy parkowania w Pradze',
      description:
        'Przeglądaj aktualne dane stref parkowania w Pradze bez rejestracji i numeru rejestracyjnego. Płatności przez Parknuto nie są jeszcze dostępne. Link do oficjalnej płatności miasta.',
    },
    partners: {
      title: 'Parknuto dla miast — przygotowanie integracji parkingowych',
      description:
        'Informacje dla miast i operatorów parkingów: wymagania umowne, techniczne i operacyjne, kolejne kroki oraz warunki przyszłego uruchomienia.',
    },
    coverage: {
      title: 'Zasięg Parknuto — miasta w przygotowaniu',
      description:
        'Płatności przez Parknuto nie są jeszcze dostępne w żadnym mieście. Miasto w przygotowaniu nie oznacza aktywnej usługi ani zawartej współpracy.',
    },
    privacy: {
      title: 'Prywatność — Parknuto',
      description:
        'Jak publiczna wersja demonstracyjna Parknuto przetwarza lokalizację, korespondencję email i logi techniczne. Analityka działa wyłącznie za Twoją zgodą.',
    },
    terms: {
      title: 'Regulamin publicznej wersji demonstracyjnej — Parknuto',
      description:
        'Zakres i warunki korzystania z publicznej wersji demonstracyjnej Parknuto. Mapa ma charakter informacyjny; strona nie wydaje uprawnień do parkowania ani nie obsługuje płatności.',
    },
  },
};

export const PUBLIC_ERROR_METADATA: Record<
  PublicLocale,
  { title: string; description: string }
> = {
  cs: {
    title: 'Stránka se nenašla — Parknuto',
    description:
      'Požadovaná stránka na této doméně neexistuje. Vraťte se na mapu zón nebo zvolte jiný jazyk.',
  },
  sk: {
    title: 'Stránka sa nenašla — Parknuto',
    description:
      'Požadovaná stránka na tejto doméne neexistuje. Vráťte sa na mapu zón alebo zvoľte iný jazyk.',
  },
  en: {
    title: 'Page not found — Parknuto',
    description:
      'This page does not exist on this domain. Return to the zone map or choose another language.',
  },
  de: {
    title: 'Seite nicht gefunden — Parknuto',
    description:
      'Diese Seite existiert auf dieser Domain nicht. Kehren Sie zur Zonenkarte zurück oder wählen Sie eine andere Sprache.',
  },
  es: {
    title: 'Página no encontrada — Parknuto',
    description:
      'Esta página no existe en este dominio. Vuelve al mapa de zonas o elige otro idioma.',
  },
  it: {
    title: 'Pagina non trovata — Parknuto',
    description:
      'Questa pagina non esiste su questo dominio. Torna alla mappa delle zone o scegli un’altra lingua.',
  },
  pl: {
    title: 'Nie znaleziono strony — Parknuto',
    description:
      'Ta strona nie istnieje w tej domenie. Wróć do mapy stref lub wybierz inny język.',
  },
};

export function getPageMetadata(
  route: PublicRoute,
  locale: PublicLocale,
  publishedLocales: readonly PublicLocale[] = PUBLISHED_PUBLIC_LOCALES,
) {
  return {
    ...metadata[locale][route],
    canonical: `${PUBLIC_ORIGINS[locale]}${publicPath(route, locale)}`,
    alternates: Object.fromEntries(
      [...publishedLocales, ...(publishedLocales.includes('en') ? ['x-default' as const] : [])].map((language) => [
        language,
        `${PUBLIC_ORIGINS[language === 'x-default' ? 'en' : language]}${publicPath(route, language === 'x-default' ? 'en' : language)}`,
      ]),
    ),
  };
}
